/**
 * Página de Gestão de Máquinas de Cartão
 * Tela auxiliar para cadastrar maquininhas e taxas por parcela (NÃO aparece no menu)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme as useAppTheme } from '../../context/ThemeContext';
import {
  Box, Typography, Paper, Button, TextField, IconButton, Tooltip,
  Alert, CircularProgress, Divider, Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import maquinaCartaoService from '../../services/maquinaCartaoService';

const ORANGE = '#F97316';
const PARCELAS_MAX = 21; // a maioria das operadoras não passa de 18-21x

export default function GerenciarMaquinasCartao() {
  const navigate = useNavigate();
  const { isDarkMode } = useAppTheme();

  const [maquinas, setMaquinas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selecionadaId, setSelecionadaId] = useState(null);
  const [nomeNova, setNomeNova] = useState('');
  const [criando, setCriando] = useState(false);
  const [taxas, setTaxas] = useState({});
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');

  const selecionada = maquinas.find(m => m.id === selecionadaId) || null;

  const carregar = async (manterSelecao = true) => {
    setLoading(true);
    try {
      const lista = await maquinaCartaoService.listar();
      const arr = Array.isArray(lista) ? lista : [];
      setMaquinas(arr);
      if (manterSelecao && selecionadaId && arr.some(m => m.id === selecionadaId)) {
        // mantém seleção atual
      } else if (arr.length > 0 && !manterSelecao) {
        setSelecionadaId(arr[0].id);
      }
    } catch (e) {
      setErro('Erro ao carregar máquinas');
    }
    setLoading(false);
  };

  useEffect(() => { carregar(false); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selecionada) {
      const mapa = {};
      (selecionada.taxas || []).forEach(t => {
        mapa[t.parcelas] = {
          taxaPercentual: t.taxaPercentual ?? '',
          taxaAntecipacaoPercentual: t.taxaAntecipacaoPercentual ?? '',
        };
      });
      setTaxas(mapa);
    } else {
      setTaxas({});
    }
  }, [selecionadaId]); // eslint-disable-line react-hooks/exhaustive-deps

  const criarMaquina = async () => {
    if (!nomeNova.trim()) return;
    setCriando(true);
    setErro('');
    try {
      const nova = await maquinaCartaoService.criar(nomeNova.trim());
      setNomeNova('');
      await carregar(true);
      setSelecionadaId(nova.id);
      setSucesso('Máquina criada!');
      setTimeout(() => setSucesso(''), 2500);
    } catch (e) {
      setErro('Erro ao criar máquina');
    }
    setCriando(false);
  };

  const removerMaquina = async (maquina) => {
    if (!window.confirm(`Remover a máquina "${maquina.nome}"? As taxas cadastradas dela também serão apagadas.`)) return;
    try {
      await maquinaCartaoService.remover(maquina.id);
      if (selecionadaId === maquina.id) setSelecionadaId(null);
      await carregar(false);
    } catch (e) {
      setErro('Erro ao remover máquina');
    }
  };

  const alterarTaxa = (parcelas, campo, valor) => {
    setTaxas(prev => ({ ...prev, [parcelas]: { ...prev[parcelas], [campo]: valor } }));
  };

  const salvarTaxas = async () => {
    if (!selecionada) return;
    setSalvando(true);
    setErro('');
    try {
      const lista = Object.entries(taxas)
        .filter(([, v]) => v.taxaPercentual !== '' && v.taxaPercentual !== null && v.taxaPercentual !== undefined)
        .map(([parcelas, v]) => ({
          parcelas: Number(parcelas),
          taxaPercentual: Number(v.taxaPercentual),
          taxaAntecipacaoPercentual: v.taxaAntecipacaoPercentual === '' ? null : Number(v.taxaAntecipacaoPercentual),
        }));
      await maquinaCartaoService.salvarTaxas(selecionada.id, lista);
      await carregar(true);
      setSucesso('Taxas salvas!');
      setTimeout(() => setSucesso(''), 2500);
    } catch (e) {
      setErro('Erro ao salvar taxas');
    }
    setSalvando(false);
  };

  return (
    <Box sx={{ p: 3, backgroundColor: isDarkMode ? undefined : '#FFFAF5', minHeight: '100vh' }}>
      {/* Cabeçalho */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <IconButton onClick={() => navigate('/faturamento')} sx={{ color: ORANGE }}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ borderLeft: '4px solid #F97316', pl: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CreditCardIcon sx={{ fontSize: 30, color: ORANGE }} /> Máquinas de Cartão
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, pl: 1.5 }}>
            Cadastre suas maquininhas e as taxas cobradas por parcela
          </Typography>
        </Box>
      </Box>

      {erro && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro('')}>{erro}</Alert>}
      {sucesso && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSucesso('')}>{sucesso}</Alert>}

      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'flex-start' }}>
        {/* Coluna esquerda: lista de máquinas */}
        <Paper elevation={0} sx={{
          width: { xs: '100%', md: 320 }, flexShrink: 0, borderRadius: 3,
          border: '1px solid', borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
          overflow: 'hidden',
        }}>
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)' }}>
            <Typography variant="subtitle2" fontWeight={700} mb={1}>Nova máquina</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small" fullWidth placeholder="Ex: Stone, Cielo Loja 2..."
                value={nomeNova}
                onChange={e => setNomeNova(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && criarMaquina()}
              />
              <Button
                variant="contained" onClick={criarMaquina} disabled={!nomeNova.trim() || criando}
                sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#EA580C' }, minWidth: 0, px: 1.5 }}
              >
                {criando ? <CircularProgress size={16} color="inherit" /> : <AddIcon fontSize="small" />}
              </Button>
            </Box>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress size={26} sx={{ color: ORANGE }} /></Box>
          ) : maquinas.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <PointOfSaleIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Nenhuma máquina cadastrada ainda.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ py: 0.5 }}>
              {maquinas.map(m => {
                const ativa = selecionadaId === m.id;
                return (
                  <Box
                    key={m.id}
                    onClick={() => setSelecionadaId(m.id)}
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      px: 2, py: 1.3, cursor: 'pointer',
                      borderLeft: ativa ? `3px solid ${ORANGE}` : '3px solid transparent',
                      bgcolor: ativa ? (isDarkMode ? 'rgba(249,115,22,0.12)' : 'rgba(249,115,22,0.08)') : 'transparent',
                      '&:hover': { bgcolor: ativa ? undefined : (isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)') },
                      transition: 'all 0.15s',
                    }}
                  >
                    <Box>
                      <Typography fontWeight={ativa ? 700 : 500} fontSize="0.9rem">{m.nome}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(m.taxas || []).length} parcela(s) configurada(s)
                      </Typography>
                    </Box>
                    <Tooltip title="Remover máquina">
                      <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); removerMaquina(m); }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                );
              })}
            </Box>
          )}
        </Paper>

        {/* Coluna direita: taxas da máquina selecionada */}
        <Paper elevation={0} sx={{
          flex: 1, width: '100%', borderRadius: 3, p: 3,
          border: '1px solid', borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
        }}>
          {!selecionada ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <CreditCardIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
              <Typography color="text.secondary">
                Selecione uma máquina à esquerda para configurar as taxas,<br />ou crie uma nova.
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="h6" fontWeight={700}>{selecionada.nome}</Typography>
                <Chip
                  size="small" label={`${(selecionada.taxas || []).length} parcela(s)`}
                  sx={{ bgcolor: 'rgba(249,115,22,0.1)', color: ORANGE, fontWeight: 600 }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" mb={2.5}>
                Preencha só as quantidades de parcela que essa máquina realmente cobra. A taxa de
                antecipação é opcional — deixe em branco se não usa.
              </Typography>

              <Divider sx={{ mb: 2 }} />

              <Box sx={{
                display: 'grid', gridTemplateColumns: '70px 1fr 1fr', gap: 1.5,
                mb: 1, px: 0.5,
              }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary">PARCELAS</Typography>
                <Typography variant="caption" fontWeight={700} color="text.secondary">TAXA NORMAL (%)</Typography>
                <Typography variant="caption" fontWeight={700} color="text.secondary">TAXA ANTECIPADA (%)</Typography>
              </Box>

              <Box sx={{ maxHeight: 420, overflowY: 'auto', pr: 0.5 }}>
                {Array.from({ length: PARCELAS_MAX }, (_, i) => i + 1).map(p => (
                  <Box key={p} sx={{
                    display: 'grid', gridTemplateColumns: '70px 1fr 1fr', gap: 1.5, alignItems: 'center',
                    mb: 1, px: 0.5, py: 0.5, borderRadius: 1.5,
                    '&:hover': { bgcolor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' },
                  }}>
                    <Typography fontWeight={600} fontSize="0.9rem">{p}x</Typography>
                    <TextField
                      size="small" type="number" placeholder="—"
                      value={taxas[p]?.taxaPercentual ?? ''}
                      onChange={e => alterarTaxa(p, 'taxaPercentual', e.target.value)}
                      inputProps={{ step: '0.01', min: 0 }}
                    />
                    <TextField
                      size="small" type="number" placeholder="—"
                      value={taxas[p]?.taxaAntecipacaoPercentual ?? ''}
                      onChange={e => alterarTaxa(p, 'taxaAntecipacaoPercentual', e.target.value)}
                      inputProps={{ step: '0.01', min: 0 }}
                    />
                  </Box>
                ))}
              </Box>

              <Button
                variant="contained" fullWidth
                startIcon={salvando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={salvarTaxas} disabled={salvando}
                sx={{ mt: 2.5, bgcolor: ORANGE, '&:hover': { bgcolor: '#EA580C' }, py: 1.1, fontWeight: 700 }}
              >
                {salvando ? 'Salvando...' : 'Salvar taxas'}
              </Button>
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
